import React from "react";
import { AlertTriangle, LifeBuoy, ShieldCheck, Ticket } from "lucide-react";
import { Skeleton } from "../../../ui/skeleton";
import { EmptyState, InsightCard, SectionHeading } from "../components/Primitives";
import { humanise } from "../utils/format";

/**
 * Section E — Bonuses, support and risk (§4.6). Three cards in one row: the
 * context an agent needs before they act on anything above.
 */

/** Ticket-style chip with the notch a real voucher has. */
function BonusChip({ code }) {
  return (
    <span className="relative inline-flex items-center gap-1.5 rounded-md border border-dashed bg-muted py-1 pl-2.5 pr-2.5 font-mono text-xs text-foreground">
      <Ticket className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
      {code}
    </span>
  );
}

function Bonuses({ codes }) {
  if (!codes || !codes.length) return <EmptyState minHeight={96} message="No bonuses claimed yet." />;
  return (
    <div className="flex flex-wrap gap-1.5">
      {codes.map((code) => (
        <BonusChip key={code} code={code} />
      ))}
    </div>
  );
}

function SupportHistory({ issues, summary }) {
  if (!issues?.length && !summary) {
    return <EmptyState minHeight={96} message="No support contacts on record." />;
  }

  return (
    <div className="space-y-3">
      {issues?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {issues.map((issue) => (
            <span
              key={issue}
              className="inline-flex items-center gap-1.5 rounded-md border border-chart-warning/40 bg-chart-warning/10 px-2 py-1 text-xs text-foreground"
            >
              <LifeBuoy className="h-3 w-3 text-chart-warning" aria-hidden="true" />
              {humanise(issue)}
            </span>
          ))}
        </div>
      )}
      {summary && (
        <blockquote className="border-l-2 pl-3 text-sm italic leading-snug text-muted-foreground">
          {summary}
        </blockquote>
      )}
    </div>
  );
}

function RiskCard({ flagged }) {
  if (flagged) {
    return (
      <div className="flex items-start gap-3 rounded-md border border-destructive/45 bg-destructive/10 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-foreground">Risk flag active</p>
          <p className="mt-1 text-xs leading-snug text-muted-foreground">
            Hold rewards, promotions and bonus grants for this player until the flag is cleared by risk or compliance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 rounded-md border bg-muted p-3">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div>
        <p className="text-sm font-semibold text-foreground">No risk flags</p>
        <p className="mt-1 text-xs leading-snug text-muted-foreground">
          Nothing on this profile blocks rewards, promotions or bonus grants.
        </p>
      </div>
    </div>
  );
}

export function BonusSupportRisk({ profile, loading }) {
  return (
    <section>
      <SectionHeading
        id="pi-history"
        eyebrow="Section E"
        title="Bonuses, support & risk"
        description="Context to check before acting on anything above."
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <InsightCard title="Bonuses claimed" subtitle="Lifetime">
          {loading ? <Skeleton className="h-9 w-full rounded-md" /> : <Bonuses codes={profile?.bonuses_claimed} />}
        </InsightCard>

        <InsightCard title="Support history" subtitle="Past issues raised">
          {loading ? (
            <Skeleton className="h-20 w-full rounded-md" />
          ) : (
            <SupportHistory issues={profile?.past_issues} summary={profile?.support_history_summary} />
          )}
        </InsightCard>

        <InsightCard title="Risk">
          {loading ? <Skeleton className="h-20 w-full rounded-md" /> : <RiskCard flagged={profile?.risk_flagged} />}
        </InsightCard>
      </div>
    </section>
  );
}
